import type { LogBridge } from '@hibro/platform-renderer';
import type { LogRecord } from '@hibro/shared';

/**
 * Electron `LogBridge` (见 15 / §5.16), over the preload `window.hibroLog` IPC
 * bridge to the main-process `LogService`.
 *
 * Lives in the app shell rather than platform-renderer so that package stays free
 * of the preload global, mirroring `iframe-bridge.ts`. The sink itself sits in the
 * **main process** deliberately — above the extension host, so it outlives the
 * ext-host crashes it records and owns the JSONL file. The browser runtime has no
 * such layer and supplies its own page-side bridge instead (`src/web/main.tsx`).
 *
 * Returns `undefined` when the preload global is absent, which is how the
 * diagnostics section, crash toast and console mirror stay unmounted rather than
 * half-working.
 */
export function createElectronLogBridge(): LogBridge | undefined {
  const api = window.hibroLog;
  if (!api) {
    return undefined;
  }
  // Cast at the boundary: the preload global is typed structurally and does not
  // carry the closed `@hibro/shared` unions (`LogLevel`/`LogSource`/`LogKind`).
  return {
    query: (...args) => api.query(...args) as Promise<LogRecord[]>,
    clear: (...args) => api.clear(...args),
    append: (...args) => api.append(...args),
    // Forwarded only when the runtime has one; the browser page stamps a
    // `hibroLog` without it, and the panel drops the button accordingly (见 15).
    ...(api.openFolder
      ? {
          openFolder: async (): Promise<void> => {
            await api.openFolder?.();
          },
        }
      : {}),
    onChanged: (callback) =>
      api.onChanged((record) => callback(record as LogRecord)),
  };
}
